import React, { useEffect, useRef, useState } from "react";
import DailyIframe from "@daily-co/daily-js";
import { DailyProvider, useDaily, useParticipantIds, useLocalSessionId, useDailyEvent, useVideoTrack } from "@daily-co/daily-react";
import { Mic, MicOff, PhoneOff, Video, VideoOff, Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Daily.co-powered call screen. Replaces the old mock InCallScreen.
 * Props: consultationId, type ('audio'|'video'), onEnd
 */
export default function DailyCall({ consultationId, type, lawyer, onEnd }) {
  const [callObject, setCallObject] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch token once + create call object once
  useEffect(() => {
    let mounted = true;
    let co = null;
    (async () => {
      try {
        const res = await api.post(`/consultations/${consultationId}/call-token`);
        if (!mounted) return;
        setTokenData(res.data);
        co = DailyIframe.createCallObject();
        setCallObject(co);
        await co.join({
          url: res.data.room_url,
          token: res.data.token,
          videoSource: type === "video",
          audioSource: true,
          userName: "You",
        });
        setLoading(false);
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Could not start call";
        setError(typeof msg === "string" ? msg : "Could not start call");
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      if (co) {
        co.leave().catch(() => {});
        co.destroy().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationId]);

  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0A2540]/95 text-white grid place-items-center p-6" data-testid="call-error-overlay">
        <div className="max-w-sm text-center">
          <AlertCircle size={48} className="mx-auto text-amber-400" />
          <h2 className="mt-4 text-xl font-extrabold" style={{ fontFamily: "Poppins" }}>Couldn't start call</h2>
          <p className="mt-2 text-sm text-white/80">{error}</p>
          <button
            onClick={onEnd}
            data-testid="call-error-close"
            className="mt-6 h-11 px-6 rounded-full adv-btn-gold font-bold"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (loading || !callObject) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0A2540] text-white grid place-items-center" data-testid="call-loading">
        <div className="text-center">
          <Loader2 size={32} className="mx-auto animate-spin text-[#D4AF37]" />
          <p className="mt-4 text-sm text-white/70">Connecting you now…</p>
        </div>
      </div>
    );
  }

  return (
    <DailyProvider callObject={callObject}>
      <CallScreen type={type} lawyer={lawyer} isAudioOnly={tokenData?.is_audio_only} onEnd={onEnd} />
    </DailyProvider>
  );
}

function CallScreen({ type, lawyer, isAudioOnly, onEnd }) {
  const daily = useDaily();
  const participantIds = useParticipantIds({ filter: "remote" });
  const localId = useLocalSessionId();
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(isAudioOnly);
  const [seconds, setSeconds] = useState(0);
  const [status, setStatus] = useState("Connecting…");

  useEffect(() => {
    const id = setInterval(() => setSeconds((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);

  useDailyEvent("joined-meeting", () => setStatus("Waiting for the other participant…"));
  useDailyEvent("participant-joined", () => setStatus("Connected"));
  useDailyEvent("participant-left", () => setStatus("Other participant left"));
  useDailyEvent("error", (e) => toast.error(e?.errorMsg || "Call error"));

  // Sync actual daily state for mute/video
  useEffect(() => {
    if (!daily) return;
    daily.setLocalAudio(!muted);
  }, [daily, muted]);
  useEffect(() => {
    if (!daily || isAudioOnly) return;
    daily.setLocalVideo(!videoOff);
  }, [daily, videoOff, isAudioOnly]);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  const remoteId = participantIds[0];
  const connected = !!remoteId;

  const hangup = async () => {
    try { await daily?.leave(); } catch {}
    onEnd?.();
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A2540] text-white flex flex-col" data-testid="in-call-screen">
      {/* Top bar */}
      <div className="px-5 py-3 flex items-center justify-between bg-black/30">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-400" : "bg-amber-400"} animate-pulse`} />
          <span className="text-xs font-bold tracking-wider uppercase">
            {connected ? `${type === "video" && !isAudioOnly ? "Video" : "Audio"} · Connected` : status}
          </span>
        </div>
        <span className="text-xs font-mono text-[#D4AF37]" data-testid="call-timer">{mm}:{ss}</span>
      </div>

      {/* Main video area */}
      <div className="flex-1 relative grid place-items-center">
        {(!connected || isAudioOnly || type === "audio") ? (
          <div className="text-center p-6">
            <div className="h-40 w-40 sm:h-48 sm:w-48 mx-auto rounded-full bg-[#122F4F] grid place-items-center">
              {lawyer?.lawyer_photo ? (
                <img src={lawyer.lawyer_photo} alt="" className="h-full w-full rounded-full object-cover" />
              ) : (
                <span className="text-5xl text-[#D4AF37] font-bold">
                  {(lawyer?.lawyer_name || "L").split(" ").slice(-2).map((x) => x[0]).join("")}
                </span>
              )}
            </div>
            <h2 className="mt-6 text-2xl font-extrabold" style={{ fontFamily: "Poppins" }}>
              {lawyer?.lawyer_name || "Your lawyer"}
            </h2>
            {!connected && <p className="text-white/60 mt-2 text-sm">{status}</p>}
          </div>
        ) : (
          <RemoteVideo sessionId={remoteId} />
        )}

        {/* PiP self-view (video mode only) */}
        {!isAudioOnly && type === "video" && localId && !videoOff && (
          <div className="absolute bottom-28 right-4 w-28 sm:w-36 aspect-video rounded-xl overflow-hidden border-2 border-white/20 bg-black" data-testid="self-video-pip">
            <LocalVideo sessionId={localId} />
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="px-6 py-6 flex items-center justify-center gap-4 bg-black/30">
        <button
          onClick={() => setMuted((x) => !x)}
          data-testid="mute-btn"
          title={muted ? "Unmute" : "Mute"}
          className={`h-14 w-14 rounded-full grid place-items-center transition-colors ${muted ? "bg-red-500/90 hover:bg-red-600" : "bg-white/10 hover:bg-white/20"}`}
        >
          {muted ? <MicOff size={22} /> : <Mic size={22} />}
        </button>
        {!isAudioOnly && type === "video" && (
          <button
            onClick={() => setVideoOff((x) => !x)}
            data-testid="video-toggle-btn"
            title={videoOff ? "Turn on camera" : "Turn off camera"}
            className={`h-14 w-14 rounded-full grid place-items-center transition-colors ${videoOff ? "bg-red-500/90 hover:bg-red-600" : "bg-white/10 hover:bg-white/20"}`}
          >
            {videoOff ? <VideoOff size={22} /> : <Video size={22} />}
          </button>
        )}
        <button
          onClick={hangup}
          data-testid="end-call-btn"
          title="End call"
          className="h-16 w-16 rounded-full bg-red-600 hover:bg-red-700 grid place-items-center shadow-xl"
        >
          <PhoneOff size={26} />
        </button>
      </div>
    </div>
  );
}

function LocalVideo({ sessionId }) {
  const videoRef = useRef(null);
  const videoState = useVideoTrack(sessionId);
  useEffect(() => {
    const track = videoState?.persistentTrack;
    if (!videoRef.current) return;
    videoRef.current.srcObject = track ? new MediaStream([track]) : null;
  }, [videoState?.persistentTrack]);
  return <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />;
}

function RemoteVideo({ sessionId }) {
  const videoRef = useRef(null);
  const videoState = useVideoTrack(sessionId);
  useEffect(() => {
    const track = videoState?.persistentTrack;
    if (!videoRef.current) return;
    videoRef.current.srcObject = track ? new MediaStream([track]) : null;
  }, [videoState?.persistentTrack]);
  return <video ref={videoRef} autoPlay playsInline className="w-full h-full object-contain bg-black" data-testid="remote-video" />;
}
